import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { buildProductSegmentIndex, type ProductSegmentIndex } from './segments'
import { authFetch } from './auth'
import type {
  AdvertisingCostData,
  ErpInventory,
  InternalProductInfo,
  JdAdvertisingOrderData,
  ProductData,
  ProductImageOverrides,
  ProductMaster,
  ProductRecord,
  ProductSegments,
  SpuNameNotes,
} from './types'

interface ProductDataContextValue {
  data: ProductData | null
  loading: boolean
  error: string
  selectedDate: string
  setSelectedDate: (date: string) => void
  productIndex: Map<string, ProductRecord>
  segments: ProductSegments | null
  productMaster: ProductMaster | null
  internalProductInfo: InternalProductInfo | null
  advertisingCosts: AdvertisingCostData | null
  jdAdvertisingOrders: JdAdvertisingOrderData | null
  erpInventory: ErpInventory | null
  segmentIndex: ProductSegmentIndex
  reloadSegments: () => Promise<void>
  reloadProductMaster: () => Promise<void>
  saveSpuNameNote: (spuId: string, note: string) => Promise<void>
  uploadProductImage: (skuId: string, file: File) => Promise<void>
}

export type ProductDataResource = 'data' | 'segments' | 'master' | 'internal' | 'advertising' | 'adOrders' | 'notes' | 'images' | 'erp'

export function missingProductDataResources(
  requested: readonly ProductDataResource[],
  loaded: ReadonlySet<ProductDataResource>,
): ProductDataResource[] {
  return requested.filter((resource) => !loaded.has(resource))
}

const ProductDataContext = createContext<ProductDataContextValue | null>(null)

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: 'no-store' })
  if (!response.ok) throw new Error(`数据加载失败：${response.status}`)
  return response.json() as Promise<T>
}

const PRODUCT_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const MAX_PRODUCT_IMAGE_BYTES = 5 * 1024 * 1024

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(String(reader.result || '')))
    reader.addEventListener('error', () => reject(new Error('图片读取失败')))
    reader.readAsDataURL(file)
  })
}

export function ProductDataProvider({ children, enabled = true, resources = ['data'] }: { children: ReactNode; enabled?: boolean; resources?: ProductDataResource[] }) {
  const [rawData, setRawData] = useState<ProductData | null>(null)
  const [segments, setSegments] = useState<ProductSegments | null>(null)
  const [productMaster, setProductMaster] = useState<ProductMaster | null>(null)
  const [internalProductInfo, setInternalProductInfo] = useState<InternalProductInfo | null>(null)
  const [advertisingCosts, setAdvertisingCosts] = useState<AdvertisingCostData | null>(null)
  const [jdAdvertisingOrders, setJdAdvertisingOrders] = useState<JdAdvertisingOrderData | null>(null)
  const [erpInventory, setErpInventory] = useState<ErpInventory | null>(null)
  const [spuNameNotes, setSpuNameNotes] = useState<SpuNameNotes | null>(null)
  const [productImageOverrides, setProductImageOverrides] = useState<ProductImageOverrides | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const loadedResources = useRef<Set<ProductDataResource>>(new Set())
  const resourceKey = resources.slice().sort().join(',')

  useEffect(() => {
    if (!enabled) {
      setRawData(null)
      setSegments(null)
      setProductMaster(null)
      setInternalProductInfo(null)
      setAdvertisingCosts(null)
      setJdAdvertisingOrders(null)
      setErpInventory(null)
      setSpuNameNotes(null)
      setProductImageOverrides(null)
      setSelectedDate('')
      loadedResources.current.clear()
      setLoading(false)
      setError('')
      return
    }
    let active = true
    setError('')
    const requested = resourceKey.split(',').filter(Boolean) as ProductDataResource[]
    const missing = new Set(missingProductDataResources(requested, loadedResources.current))
    if (!missing.size) {
      setLoading(false)
      return
    }
    setLoading(missing.has('data') && !rawData)
    const load = <T,>(key: ProductDataResource, url: string, apply: (payload: T) => void) => {
      if (!missing.has(key)) return Promise.resolve()
      return fetchJson<T>(url).then((payload) => {
        if (!active) return
        apply(payload)
        loadedResources.current.add(key)
      })
    }
    Promise.all([
      load<ProductData>('data', '/data/product-data.json', (payload) => {
        setRawData(payload)
        setSelectedDate((current) => current || payload.meta.maxDate || '')
      }),
      load<ProductSegments>('segments', '/data/product-segments.json', setSegments),
      load<ProductMaster>('master', '/data/product-master.json', setProductMaster),
      load<InternalProductInfo>('internal', '/data/internal-product-info.json', setInternalProductInfo),
      load<AdvertisingCostData>('advertising', '/data/advertising-costs.json', setAdvertisingCosts),
      load<JdAdvertisingOrderData>('adOrders', '/data/jd-advertising-orders.json', setJdAdvertisingOrders),
      load<SpuNameNotes>('notes', '/data/spu-name-notes.json', setSpuNameNotes),
      load<ProductImageOverrides>('images', '/data/product-image-overrides.json', setProductImageOverrides),
      load<ErpInventory>('erp', '/data/erp-inventory.json', setErpInventory),
    ])
      .catch((reason: unknown) => active && setError(reason instanceof Error ? reason.message : String(reason)))
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [enabled, resourceKey])

  const reloadSegments = useCallback(async () => {
    const payload = await fetchJson<ProductSegments>('/data/product-segments.json')
    setSegments(payload)
  }, [])

  const reloadProductMaster = useCallback(async () => {
    const payload = await fetchJson<ProductMaster>('/data/product-master.json')
    setProductMaster(payload)
  }, [])

  const saveSpuNameNote = useCallback(async (spuId: string, note: string) => {
    const response = await authFetch('/api/spu-name-notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ spuId, note }),
    })
    const payload = await response.json() as SpuNameNotes & { error?: string }
    if (!response.ok) throw new Error(payload.error || `保存失败：${response.status}`)
    setSpuNameNotes(payload)
  }, [])

  const uploadProductImage = useCallback(async (skuId: string, file: File) => {
    if (!PRODUCT_IMAGE_TYPES.has(file.type)) throw new Error('只支持 JPG、PNG 或 WebP 图片')
    if (!file.size || file.size > MAX_PRODUCT_IMAGE_BYTES) throw new Error('图片为空或超过5MB')
    const dataUrl = await readFileAsDataUrl(file)
    const response = await authFetch('/api/product-images', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skuId, fileName: file.name, dataUrl }),
    })
    const payload = await response.json() as ProductImageOverrides & { error?: string }
    if (!response.ok) throw new Error(payload.error || `上传失败：${response.status}`)
    setProductImageOverrides(payload)
  }, [])

  const data = useMemo(() => {
    if (!rawData) return null
    const notes = spuNameNotes?.notes || {}
    const images = productImageOverrides?.images || {}
    if (!Object.keys(notes).length && !Object.keys(images).length) return rawData
    return {
      ...rawData,
      products: rawData.products.map((product) => {
        const note = product.level === 'spu' ? (notes[product.id] || '').trim() : ''
        const imageOverride = product.level === 'sku' ? images[product.id] : undefined
        if (!note && !imageOverride) return product
        return {
          ...product,
          ...(note ? { nameNote: note } : {}),
          ...(imageOverride ? { image: imageOverride.path, imageLabel: '本地上传图片' } : {}),
        }
      }),
    }
  }, [productImageOverrides, rawData, spuNameNotes])

  const productIndex = useMemo(() => new Map(
    (data?.products || []).map((product) => [`${product.level}:${product.id}`, product]),
  ), [data])
  const segmentIndex = useMemo(() => buildProductSegmentIndex(data, segments), [data, segments])
  const effectiveLoading = enabled && (loading || (!rawData && !error))

  return (
    <ProductDataContext.Provider value={{
      data,
      loading: effectiveLoading,
      error,
      selectedDate,
      setSelectedDate,
      productIndex,
      segments,
      productMaster,
      internalProductInfo,
      advertisingCosts,
      jdAdvertisingOrders,
      erpInventory,
      segmentIndex,
      reloadSegments,
      reloadProductMaster,
      saveSpuNameNote,
      uploadProductImage,
    }}>
      {children}
    </ProductDataContext.Provider>
  )
}

export function useProductData() {
  const context = useContext(ProductDataContext)
  if (!context) throw new Error('useProductData must be used within ProductDataProvider')
  return context
}
